import classNames from 'classnames';
import { useMutation } from 'react-query';
import { RxCross2 } from 'react-icons/rx';
import FirstStepForm from './FirstStepForm';
import { Step } from '../MentorMeetingCards';
import SecondStepForm from './SecondStepForm';
import { Button, Modal } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { Meeting, MentorFeedbackCreateInfo, UIMeeting } from 'types';
import { updateLastUnseenMeetingId } from 'src/apis/user';
import { useQueryClient } from 'react-query';
import { addRatingByIdMentor } from 'src/apis/meetingFeedback';
import { get } from 'lodash';
import moment from 'moment';

interface NPSmodalProps {
	isOpen: boolean;
	onClose: () => void;
	rating: number;
	setRating: (arg: number) => void;
	mentorFeedback: MentorFeedbackCreateInfo;
	setMentorFeedback: (args: MentorFeedbackCreateInfo) => void;
	meeting: UIMeeting;
	isEditable: boolean;
	step: Step;
	setStep: (option: Step) => void;
	lastUnseenMeetingId: string;
}

const NPSmodal = ({
	meeting,
	isOpen,
	onClose,
	isEditable,
	rating,
	setRating,
	mentorFeedback,
	step,
	setStep,
	setMentorFeedback,
	lastUnseenMeetingId,
}: NPSmodalProps) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const feedbackOptions = [
		{ label: 'Interviewee No-Show' },
		{ label: 'Audio/Video issues' },
		{ label: 'Assessment question not updating' },
		{ label: 'No feedback questions' },
		{ label: 'Connectivity issues' },
		{ label: 'None' },
	];
	const addFeedbackByMentorIdFn = useMutation({
		mutationKey: 'addFeedbackByMentorId',
		mutationFn: addRatingByIdMentor,
		onSuccess() {
			setIsLoading(false);
			setStep(Step.StepOne);
			queryClient.refetchQueries(['getAllMeetings']);
			onClose();
			toast({
				title: 'Feedback Submitted',
				type: 'success',
				duration: 2000,
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
			onClose();
			setStep(Step.StepOne);
		},
	});

	const updateUnseenMeetingIdfn = useMutation(
		['updateLastUnseenMeetingId'],
		async (id: string | null) => await updateLastUnseenMeetingId(id),
		{
			onSuccess() {
				// queryClient.refetchQueries(['getAllMeetings']);
				queryClient.refetchQueries(['verifyCookie']);
			},
			onError(e: any) {
				toast({
					title: 'Something went wrong',
					type: 'error',
					duration: 10000,
				});
			},
		},
	);

	const handleSubmit = () => {
		setIsLoading(true);
		addFeedbackByMentorIdFn.mutate({ meetingId: String(meeting?._id) || '', createInfo: mentorFeedback });
		updateUnseenMeetingIdfn.mutate(null);
	};

	const handleRating = (rate: number) => {
		if (isEditable) {
			setRating(rate);
			setMentorFeedback({ ...mentorFeedback, score: rate });
		}
	};

	const handleOptionSelect = (option: string) => {
		if (isEditable) {
			const optionIndex = selectedOptions.indexOf(option);

			if (option === 'None') {
				if (selectedOptions.includes('None')) {
					setSelectedOptions([]);
				} else {
					setSelectedOptions(['None']);
				}
			} else if (optionIndex > -1) {
				setSelectedOptions(prevOptions => prevOptions.filter(item => item !== option));
			} else {
				setSelectedOptions(prevOptions => {
					if (prevOptions.includes('None')) {
						return [option];
					} else {
						return [...prevOptions, option];
					}
				});
			}
		}
	};

	useEffect(() => {
		setMentorFeedback({
			...mentorFeedback,
			issuesFaced: selectedOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptions]);

	return (
		<Modal show={isOpen} size="2xl" onClose={onClose} position="center">
			<div className="border-b p-[20px] py-[14px] dark:bg-[#1f2a37]">
				<p className="pl-[8px] font-inter text-[24px] font-[500]">Rate Your Experience</p>
				<div>
					<p className="pl-[8px] font-semibold">
						Title :- <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{meeting?.title}</span>
					</p>
					<div className="flex flex-wrap justify-between">
						<p className="pl-[8px] font-semibold">
							Candidate Name :-{' '}
							<span className="text-sm font-medium text-gray-700 dark:text-gray-300">{meeting?.participant?.name}</span>
						</p>
						<div className="flex items-center gap-2">
							<p className="pl-[8px] font-semibold">Start Time :-</p>
							<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
								{moment(meeting?.startTime).format('DD MMM YYYY, hh:mm a')}
							</span>
						</div>
					</div>
				</div>
			</div>
			<Modal.Body className="max-h-[85vh] overflow-y-scroll dark:bg-[#1f2a37]">
				{step === Step.StepOne ? (
					<FirstStepForm
						rating={rating}
						handleRating={handleRating}
						isEditable={isEditable}
						setMentorFeedback={setMentorFeedback}
						mentorFeedback={mentorFeedback}
						meeting={meeting as Meeting}
					/>
				) : (
					<SecondStepForm
						handleOptionSelect={handleOptionSelect}
						feedbackOptions={feedbackOptions}
						isEditable={isEditable}
						setMentorFeedback={setMentorFeedback}
						mentorFeedback={mentorFeedback}
					/>
				)}

				{isEditable ? (
					<div className="mt-2 flex items-center justify-end gap-3">
						{Step.StepOne !== step ? (
							<Button
								color="light"
								onClick={() => {
									setStep(Step.StepOne);
								}}
							>
								Prev
							</Button>
						) : null}
						<Button
							color="purplePlaced"
							onClick={() => {
								if (step === Step.StepOne) {
									setStep(Step.StepTwo);
								} else if (step === Step.StepTwo) {
									handleSubmit();
								}
							}}
							disabled={
								!mentorFeedback.questionRelevance ||
								!mentorFeedback.wasScoreCalculatedCorrectly ||
								!mentorFeedback.predictedSalary ||
								!mentorFeedback.candidateIntent ||
								!mentorFeedback.score ||
								isLoading
							}
						>
							{step === Step.StepTwo ? 'Submit' : 'Next'}
						</Button>
					</div>
				) : (
					<div className="mt-2 flex items-center justify-end gap-3">
						{step === Step.StepOne ? (
							<Button
								color="purplePlaced"
								onClick={() => {
									setStep(Step.StepTwo);
								}}
							>
								Next
							</Button>
						) : (
							<Button
								color="purplePlaced"
								onClick={() => {
									setStep(Step.StepOne);
								}}
							>
								Prev
							</Button>
						)}
					</div>
				)}
			</Modal.Body>

			<RxCross2
				onClick={() => {
					if (lastUnseenMeetingId && lastUnseenMeetingId === meeting._id) {
						updateUnseenMeetingIdfn.mutate(null);
					}
					onClose();
				}}
				className={`absolute right-[16px] top-[16px] h-[20px] w-[20px] cursor-pointer text-[#9CA3AF]`}
			/>
		</Modal>
	);
};

export default NPSmodal;
